/* eslint-disable @angular-eslint/no-input-rename */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { forEach } from 'lodash';
import { fromEvent, tap } from 'rxjs';
import { ScrollingBarsComponent } from '../scrolling-bars/scrolling-bars.component';
@Component({
  selector: 'app-scroller-dots',
  templateUrl: './scroller-dots.component.html',
  styleUrls: ['./scroller-dots.component.scss'],

  imports: [CommonModule, ScrollingBarsComponent],
})
export class ScrollerDotsComponent implements AfterViewInit, OnChanges, OnDestroy {
  list = viewChild<ElementRef>('list');
  element = viewChild<ElementRef>('element');
  onEndScrolling = output<boolean>();

  scrollTo = input<number>(0);
  numBars = input.required<number>();
  lateralScale = input<number>();
  hideDotsIfMobile = input<boolean>(false);

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.mobile.set(window.innerWidth < 500);
  }
  mobile = signal(false);

  private childElements;
  parent_xLeft = 1;
  parent_xRight = 1;
  scrool$;
  perc = signal(0);
  listener;

  /**
   * Lifecycle hook that is called when any data-bound property of a directive changes.
   * @param changes - Object of SimpleChanges that holds current and previous values of changed properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.setChildren();
    setTimeout(() => {
      changes['scrollTo'] && this.scrollToIndex(changes['scrollTo'].currentValue);
    }, 500);
  }

  /**
   * Lifecycle hook that is called after a component's view has been fully initialized.
   */
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setChildren();
      this.start();
    }, 300);

    this.onResize();
  }

  /**
   * Lifecycle hook that is called when a directive, pipe, or service is destroyed.
   */
  ngOnDestroy(): void {
    this.listener && this.listener();
  }

  /**
   * Sets the child elements of the list.
   */
  setChildren() {
    if (this.list()?.nativeElement) this.childElements = [].slice.call(this.list()?.nativeElement.children);
  }

  /**
   * Initializes the component by setting parent node boundaries and starting the scroll task.
   */
  start() {
    const _parent_node = this.list().nativeElement.getBoundingClientRect();
    this.parent_xLeft = _parent_node.x;
    this.parent_xRight = _parent_node.width + this.parent_xLeft;
    this.scrollTask();
    this.scrool$ = fromEvent(this.element().nativeElement, 'scroll').pipe(tap(() => this.scrollTask()));
  }

  /**
   * Handles the scroll task by calculating the scroll percentage and scaling the child elements.
   */
  scrollTask() {
    const getPerc = el => {
      const rect = el.getBoundingClientRect();
      const x_left = rect.x;
      const x_right = rect.width + x_left;
      if (x_left < this.parent_xLeft) {
        return 1 - (this.parent_xLeft - x_left) / rect.width;
      } else if (x_right > this.parent_xRight) {
        return 1 - (x_right - this.parent_xRight) / rect.width;
      } else return 1;
    };
    const window_width = this.element().nativeElement.offsetWidth;
    const scrollWidth = this.element().nativeElement.scrollWidth;
    const scrollLeft = this.element().nativeElement.scrollLeft;
    this.perc.set(+((scrollLeft / (scrollWidth - window_width)) * 100).toFixed(0));
    const _lateralScale = this.lateralScale();
    forEach(this.childElements, ele => {
      const native = ele;
      const perc = _lateralScale + (1 - _lateralScale) * getPerc(native);
      native.style.setProperty('transform', `scaleY(${perc < _lateralScale ? _lateralScale : perc})`);
    });
  }

  /**
   * Selects a bar and scrolls to its position.
   * @param bar - The index of the bar to scroll to.
   */
  selectBar(bar: number) {
    const window_width = this.element().nativeElement.offsetWidth;
    const scrollWidth = this.element().nativeElement.scrollWidth;
    const total_w = scrollWidth - window_width;
    const widthPerBar = total_w / this.childElements.length;
    const scrollLeft = bar == 0 ? 0 : bar == this.childElements.length - 1 ? total_w : bar * widthPerBar;
    this.element().nativeElement.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }

  /**
   * Scrolls to a specific index.
   * @param index - The index to scroll to.
   */
  scrollToIndex(index: number) {
    if (this.element()?.nativeElement && this.childElements) {
      const scroolLeft = (this.element().nativeElement.scrollWidth / this.childElements.length) * index;
      this.setChildren();
      this.element().nativeElement.scrollTo({
        left: scroolLeft,
        behavior: 'smooth',
      });
    }
  }
}
